/**
 * CostSchemeToolbar — the header strip above the board/list. Shows the whole-scheme cost summary
 * (Pax / Grp / Tot per pax, each as a *calculation · on-site* pair in the domestic currency), an optional
 * "revert to inheritance" action (only on a term that currently overrides its serie), and the board/list
 * view toggle. Styled with Tailwind `phx-*` tokens.
 */
import { useState } from 'react';
import { Info, LayoutGrid, List, Redo2, Undo2 } from 'lucide-react';
import type { TFunction } from 'i18next';

import type { FixedExchangeRateDto } from '@/api/generated/schemas';
import { formatMoney } from '@/shared/lib/money';
import { cn } from '@/shared/lib/utils';
import { Money } from '@/shared/ui';
import type { CostTotals } from './model';
import { RateListDialog } from './RateListDialog';

type ViewMode = 'board' | 'list';

interface CostSchemeToolbarProps {
  t: TFunction;
  totals: CostTotals;
  pax: number;
  /** Fixovaný kurzovní lístek série pro read-only popup u souhrnu. */
  exchangeRates: FixedExchangeRateDto[];
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  onUndo: () => void;
  undoDisabled: boolean;
  onRedo: () => void;
  redoDisabled: boolean;
  /** Term that overrides its serie can revert to inheritance; hidden otherwise. */
  showRevert: boolean;
  onRevert: () => void;
  revertDisabled: boolean;
}

const statsPill =
  'inline-flex items-center gap-2 rounded-phx border border-phx-border bg-phx-surface px-2.5 py-1 text-phx-sm text-phx-text-2';
const statsLabel = 'text-phx-2xs font-semibold uppercase tracking-wide text-phx-text-muted';
const moneyValue = 'font-mono font-semibold';
const viewButton =
  'inline-flex h-5.5 cursor-pointer items-center gap-1.5 rounded-phx-sm border-none bg-transparent px-2.5 text-phx-sm font-medium transition-colors';
const historyButton =
  'text-phx-text-2 hover:bg-phx-surface-2 hover:text-phx-text [&_svg]:text-phx-text-muted inline-flex h-5.5 w-6.5 cursor-pointer items-center justify-center rounded-phx-sm border-none bg-transparent transition-colors disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent disabled:[&_svg]:text-phx-text-muted';

/** One header cost figure: caps label + a calculation · on-site value pair with a trailing currency. */
function CostStat({
  label,
  calc,
  onsite,
  currency,
  total,
  title,
}: {
  label: string;
  calc: number;
  onsite: number;
  currency: string;
  total?: boolean;
  title: string;
}) {
  return (
    <span
      className={cn(statsPill, total && 'border-phx-primary/50 bg-phx-primary-tint')}
      title={title}
    >
      <span className={statsLabel}>{label}</span>
      <span className="inline-flex items-baseline gap-1 whitespace-nowrap">
        <Money value={calc} decimals={0} className={cn(moneyValue, 'text-phx-info')} />
        <span className="text-phx-text-muted">·</span>
        <Money value={onsite} decimals={0} className={cn(moneyValue, 'text-phx-warning')} />
        <span className="text-phx-text-muted text-phx-xs">{currency}</span>
      </span>
    </span>
  );
}

export function CostSchemeToolbar({
  t,
  totals,
  pax,
  exchangeRates,
  viewMode,
  onViewModeChange,
  onUndo,
  undoDisabled,
  onRedo,
  redoDisabled,
  showRevert,
  onRevert,
  revertDisabled,
}: CostSchemeToolbarProps) {
  const cur = totals.currency;
  const money = (value: number) => formatMoney(value);
  const [ratesOpen, setRatesOpen] = useState(false);

  return (
    <div className="bg-phx-surface-2 border-phx-border flex shrink-0 flex-wrap items-center gap-2 border-b px-4.5 py-2">
      <CostStat
        label={t('costScheme.costStats.pax')}
        calc={totals.paxCalc}
        onsite={totals.paxOnsite}
        currency={cur}
        title={t('costScheme.costStats.paxTooltip', {
          calc: money(totals.paxCalc),
          onsite: money(totals.paxOnsite),
          currency: cur,
        })}
      />
      <CostStat
        label={t('costScheme.costStats.grp')}
        calc={totals.grpCalc}
        onsite={totals.grpOnsite}
        currency={cur}
        title={t('costScheme.costStats.grpTooltip', {
          calc: money(totals.grpCalc),
          onsite: money(totals.grpOnsite),
          currency: cur,
        })}
      />
      <CostStat
        label={t('costScheme.costStats.totPerPax', { pax })}
        calc={totals.totPaxCalc}
        onsite={totals.totPaxOnsite}
        currency={cur}
        total
        title={t('costScheme.costStats.totTooltip', {
          pax,
          calc: money(totals.totPaxCalc),
          onsite: money(totals.totPaxOnsite),
          currency: cur,
        })}
      />
      <button
        type="button"
        onClick={() => setRatesOpen(true)}
        title={t('costScheme.rates.open')}
        aria-label={t('costScheme.rates.open')}
        className="text-phx-text-muted hover:bg-phx-surface-2 hover:text-phx-text rounded-phx-sm inline-flex h-6 w-6 cursor-pointer items-center justify-center border-none bg-transparent transition-colors"
      >
        <Info size={15} />
      </button>
      <RateListDialog
        open={ratesOpen}
        onClose={() => setRatesOpen(false)}
        domesticCurrency={cur}
        rates={exchangeRates}
      />
      <span className="flex-1" />
      {showRevert ? (
        <button
          type="button"
          onClick={onRevert}
          disabled={revertDisabled}
          className="rounded-phx text-phx-text-2 hover:bg-phx-surface-2 hover:text-phx-text active:bg-phx-surface-3 text-phx-sm inline-flex h-6.5 cursor-pointer items-center gap-1.5 border border-transparent bg-transparent px-2 font-medium transition-colors disabled:cursor-default disabled:opacity-50"
        >
          {t('costScheme.revertToInheritance')}
        </button>
      ) : null}
      <div className="border-phx-border bg-phx-surface rounded-phx inline-flex gap-0.5 border p-0.5">
        <button
          type="button"
          className={historyButton}
          onClick={onUndo}
          disabled={undoDisabled}
          title={t('costScheme.undo')}
          aria-label={t('costScheme.undo')}
        >
          <Undo2 size={14} />
        </button>
        <button
          type="button"
          className={historyButton}
          onClick={onRedo}
          disabled={redoDisabled}
          title={t('costScheme.redo')}
          aria-label={t('costScheme.redo')}
        >
          <Redo2 size={14} />
        </button>
      </div>
      <div
        className="border-phx-border bg-phx-surface rounded-phx inline-flex gap-0.5 border p-0.5"
        role="tablist"
        aria-label={t('costScheme.viewLabel')}
      >
        <button
          type="button"
          className={cn(
            viewButton,
            viewMode === 'board'
              ? 'bg-phx-primary text-phx-primary-fg [&_svg]:text-phx-primary-fg'
              : 'text-phx-text-2 hover:bg-phx-surface-2 hover:text-phx-text [&_svg]:text-phx-text-muted'
          )}
          role="tab"
          aria-selected={viewMode === 'board'}
          onClick={() => onViewModeChange('board')}
          title={t('costScheme.viewBoard')}
        >
          <LayoutGrid size={14} />
          {t('costScheme.viewBoard')}
        </button>
        <button
          type="button"
          className={cn(
            viewButton,
            viewMode === 'list'
              ? 'bg-phx-primary text-phx-primary-fg [&_svg]:text-phx-primary-fg'
              : 'text-phx-text-2 hover:bg-phx-surface-2 hover:text-phx-text [&_svg]:text-phx-text-muted'
          )}
          role="tab"
          aria-selected={viewMode === 'list'}
          onClick={() => onViewModeChange('list')}
          title={t('costScheme.viewList')}
        >
          <List size={14} />
          {t('costScheme.viewList')}
        </button>
      </div>
    </div>
  );
}
